/**
 * Estimate tab — full CRUD for scope line-items + per-project estimate config.
 *
 * Features:
 * - Catalog picker modal: search by text/keywords, grouped by category,
 *   selecting pre-fills description / unit / default_unit_price.
 * - Room picker in add & edit forms.
 * - Inline item edit: description, code, qty, unit, unit_price with live
 *   line_total preview. "Reset to catalog price" when item has a known code.
 * - Qty stepper −/+ on each row for quick adjustments without opening editor.
 * - Reject/restore toggle and delete per row.
 * - Confidence badge for AI-sourced items.
 * - Editable overhead / profit / tax % in Totals section.
 * - onUpdate callback re-fetches just the manifest and re-renders the tab.
 */
import { api } from "../../api/client.ts";
import type {
  EstimateConfig,
  IcProject,
  Manifest,
  ManifestRoom,
  ManifestScopeItem,
  ScopeCatalog,
  ScopeCatalogItem,
} from "../../types.ts";

const SOURCE_ORDER = ["template", "predefined", "auto", "manual"] as const;

const SOURCE_LABELS: Record<string, string> = {
  template: "Template",
  predefined: "Predefined (room-attached)",
  auto: "Auto (calculated)",
  manual: "Manual",
};

const SOURCE_COLORS: Record<string, string> = {
  template: "#888",
  predefined: "#4a9eff",
  auto: "#c79bff",
  manual: "#ffaa4a",
};

export interface EstimateTabOptions {
  container: HTMLElement;
  manifest: Manifest | null;
  project: IcProject;
  onUpdate?: () => Promise<void>;
}

export class EstimateTab {
  private catalog: ScopeCatalog | null = null;
  /** Flat index: code → ScopeCatalogItem for O(1) lookup. */
  private catalogByCode = new Map<string, ScopeCatalogItem>();

  constructor(private opts: EstimateTabOptions) {
    // Load catalog in background; re-render if it arrives before user opens picker.
    api.ic.getScopeCatalog().then((cat) => {
      this.catalog = cat;
      this.catalogByCode.clear();
      for (const cat_entry of cat.categories) {
        for (const item of cat_entry.items) {
          this.catalogByCode.set(item.code, item);
        }
      }
    }).catch(() => { /* catalog unavailable — graceful degradation */ });

    this.render();
  }

  private render(): void {
    const root = this.opts.container;
    root.innerHTML = "";
    root.style.cssText = "padding:16px;display:flex;flex-direction:column;gap:16px;";

    const m = this.opts.manifest;
    if (!m) {
      root.appendChild(emptyMessage("No manifest snapshot — estimate will appear when mobile pushes one."));
      return;
    }

    const roomById = new Map<string, ManifestRoom>();
    for (const r of m.rooms) roomById.set(r.id, r);

    const byGroup = new Map<string, ManifestScopeItem[]>();
    for (const item of m.scope) {
      const k = item.source || "manual";
      if (!byGroup.has(k)) byGroup.set(k, []);
      byGroup.get(k)!.push(item);
    }

    const groupKeys: string[] = [];
    for (const k of SOURCE_ORDER) if (byGroup.has(k)) groupKeys.push(k);
    for (const k of Array.from(byGroup.keys()).sort()) {
      if (!groupKeys.includes(k)) groupKeys.push(k);
    }
    if (!groupKeys.includes("manual")) groupKeys.push("manual");

    if (m.scope.length === 0) {
      root.appendChild(emptyMessage("No scope items yet."));
    }

    for (const key of groupKeys) {
      if (key === "manual") {
        root.appendChild(this.renderManualGroup(byGroup.get("manual") || [], roomById));
      } else {
        root.appendChild(this.renderGroup(key, byGroup.get(key)!, roomById));
      }
    }

    root.appendChild(this.renderTotals());
    root.appendChild(this.renderActions());
  }

  // ── Group rendering ────────────────────────────────────────────────────────

  private renderGroup(
    source: string,
    items: ManifestScopeItem[],
    roomById: Map<string, ManifestRoom>,
  ): HTMLElement {
    const groupTotal = items.filter((i) => !i.rejected).reduce((s, i) => s + (i.line_total ?? 0), 0);
    const card = makeCard();
    card.appendChild(makeGroupHeader(source, items.length, groupTotal));
    const list = document.createElement("div");
    list.style.cssText = "display:flex;flex-direction:column;";
    for (const item of items) list.appendChild(this.renderItem(item, roomById, list));
    card.appendChild(list);
    return card;
  }

  private renderManualGroup(
    items: ManifestScopeItem[],
    roomById: Map<string, ManifestRoom>,
  ): HTMLElement {
    const groupTotal = items.filter((i) => !i.rejected).reduce((s, i) => s + (i.line_total ?? 0), 0);
    const card = makeCard();
    card.appendChild(makeGroupHeader("manual", items.length, groupTotal));
    const list = document.createElement("div");
    list.style.cssText = "display:flex;flex-direction:column;";
    for (const item of items) list.appendChild(this.renderItem(item, roomById, list));
    card.appendChild(list);
    card.appendChild(this.renderAddItemForm(roomById));
    return card;
  }

  // ── Item row (view mode) ───────────────────────────────────────────────────

  private renderItem(
    item: ManifestScopeItem,
    roomById: Map<string, ManifestRoom>,
    listEl: HTMLElement,
  ): HTMLElement {
    const row = document.createElement("div");
    row.dataset.scopeId = item.id;
    row.style.cssText = `
      display:grid;grid-template-columns:minmax(0,1fr) auto;
      gap:6px 8px;padding:8px 14px;border-top:1px solid var(--border);
      align-items:start;${item.rejected ? "opacity:.45;" : ""}
    `;

    // Left: main info
    const main = document.createElement("div");
    main.style.cssText = "display:flex;flex-direction:column;gap:3px;min-width:0;";

    const titleLine = document.createElement("div");
    titleLine.style.cssText = "display:flex;align-items:baseline;gap:6px;flex-wrap:wrap;";

    if (item.code) {
      const codeEl = document.createElement("code");
      codeEl.textContent = item.code;
      codeEl.style.cssText =
        "font-size:11px;background:var(--bg);border:1px solid var(--border);padding:1px 6px;border-radius:4px;color:var(--text-muted);font-family:inherit;flex-shrink:0;";
      if (item.rejected) codeEl.style.textDecoration = "line-through";
      titleLine.appendChild(codeEl);
    }

    const desc = document.createElement("span");
    desc.textContent = item.description || "(no description)";
    desc.style.cssText = `font-size:13px;color:var(--text);word-break:break-word;${item.rejected ? "text-decoration:line-through;" : ""}`;
    titleLine.appendChild(desc);

    if (item.rejected) {
      titleLine.appendChild(badge("rejected", "rgba(255,74,74,.18)", "#ff4a4a"));
    }

    // Confidence badge for AI items
    if ((item.source === "ai" || item.source === "ai_draft") && item.confidence != null) {
      const pct = Math.round(item.confidence * 100);
      const color = pct >= 80 ? "#4aff8c" : pct >= 60 ? "#ffcc4a" : "#ff7c4a";
      titleLine.appendChild(badge(`AI ${pct}%`, `${color}22`, color));
    }

    main.appendChild(titleLine);

    // Sub-line: room · qty × unit @ price · notes
    const sub: string[] = [];
    if (item.room_id) {
      const r = roomById.get(item.room_id);
      sub.push(r?.name || `room ${item.room_id.slice(0, 6)}`);
    } else if (item.room_name) {
      sub.push(item.room_name);
    }
    if (item.quantity != null && item.unit) sub.push(`${formatNumber(item.quantity)} ${item.unit}`);
    else if (item.quantity != null) sub.push(formatNumber(item.quantity));
    if (item.unit_price != null) sub.push(`@ ${formatMoney(item.unit_price)}`);
    if (item.notes) sub.push(item.notes);
    if (sub.length) {
      const subLine = document.createElement("div");
      subLine.textContent = sub.join(" · ");
      subLine.style.cssText = "font-size:11px;color:var(--text-muted);word-break:break-word;";
      main.appendChild(subLine);
    }
    row.appendChild(main);

    // Right: total + stepper + action buttons
    const right = document.createElement("div");
    right.style.cssText = "display:flex;flex-direction:column;align-items:flex-end;gap:4px;";

    const total = document.createElement("div");
    total.textContent = item.line_total != null ? formatMoney(item.line_total) : "—";
    total.style.cssText =
      "font-size:13px;font-weight:600;font-variant-numeric:tabular-nums;color:var(--text);min-width:80px;text-align:right;";
    right.appendChild(total);

    // Qty stepper
    if (item.quantity != null) {
      const stepper = document.createElement("div");
      stepper.style.cssText = "display:flex;align-items:center;gap:2px;";
      const minusBtn = stepperBtn("−");
      const plusBtn = stepperBtn("+");
      const qtyDisplay = document.createElement("span");
      qtyDisplay.textContent = formatNumber(item.quantity);
      qtyDisplay.style.cssText =
        "font-size:11px;color:var(--text-muted);min-width:36px;text-align:center;font-variant-numeric:tabular-nums;";
      minusBtn.addEventListener("click", () =>
        this.stepQty(item, -1, minusBtn, plusBtn, qtyDisplay, total),
      );
      plusBtn.addEventListener("click", () =>
        this.stepQty(item, 1, minusBtn, plusBtn, qtyDisplay, total),
      );
      stepper.appendChild(minusBtn);
      stepper.appendChild(qtyDisplay);
      stepper.appendChild(plusBtn);
      right.appendChild(stepper);
    }

    // Edit / reject / delete buttons
    const btnRow = document.createElement("div");
    btnRow.style.cssText = "display:flex;gap:4px;";

    const editBtn = iconBtn("✎", "Edit");
    editBtn.addEventListener("click", () => this.openEditInline(row, item, roomById, listEl));
    btnRow.appendChild(editBtn);

    const rejectBtn = iconBtn(item.rejected ? "↩" : "✕", item.rejected ? "Restore" : "Reject");
    rejectBtn.style.color = item.rejected ? "#4a9eff" : "#ff7c7c";
    rejectBtn.addEventListener("click", () => this.toggleReject(item, !item.rejected));
    btnRow.appendChild(rejectBtn);

    const delBtn = iconBtn("🗑", "Delete");
    delBtn.addEventListener("click", () => this.deleteItem(item));
    btnRow.appendChild(delBtn);

    right.appendChild(btnRow);
    row.appendChild(right);
    return row;
  }

  // ── Qty stepper ────────────────────────────────────────────────────────────

  private async stepQty(
    item: ManifestScopeItem,
    delta: number,
    minusBtn: HTMLButtonElement,
    plusBtn: HTMLButtonElement,
    qtyDisplay: HTMLElement,
    totalDisplay: HTMLElement,
  ): Promise<void> {
    const cur = item.quantity ?? 0;
    const next = Math.max(0, +(cur + delta).toFixed(4));
    if (next === cur) return;

    // Optimistic update
    qtyDisplay.textContent = formatNumber(next);
    const price = item.unit_price ?? 0;
    totalDisplay.textContent = next > 0 && price > 0
      ? formatMoney(+(next * price).toFixed(2))
      : "—";
    minusBtn.disabled = true;
    plusBtn.disabled = true;

    try {
      await api.ic.patchScopeItem(this.opts.project.project_id, item.id, { quantity: next });
      await this.opts.onUpdate?.();
    } catch {
      // Revert on failure
      qtyDisplay.textContent = formatNumber(cur);
      totalDisplay.textContent = item.line_total != null ? formatMoney(item.line_total) : "—";
      minusBtn.disabled = false;
      plusBtn.disabled = false;
    }
  }

  // ── Inline edit ────────────────────────────────────────────────────────────

  private openEditInline(
    row: HTMLElement,
    item: ManifestScopeItem,
    roomById: Map<string, ManifestRoom>,
    _listEl: HTMLElement,
  ): void {
    row.innerHTML = "";
    row.style.cssText =
      "display:flex;flex-direction:column;gap:8px;padding:10px 14px;border-top:1px solid var(--border);background:var(--surface2);";

    const catalogItem = item.code ? this.catalogByCode.get(item.code) : undefined;
    const defaultPrice = catalogItem?.default_unit_price ?? null;

    const grid = document.createElement("div");
    grid.style.cssText = "display:grid;grid-template-columns:1fr 1fr;gap:6px;";

    const descInput = inlineInput("Description *", item.description || "");
    grid.appendChild(labelWrap("Description", descInput, "1 / -1"));

    const codeInput = inlineInput("Code", item.code || "");
    grid.appendChild(labelWrap("Code", codeInput));

    // Room picker
    const roomSel = buildRoomSelect(Array.from(roomById.values()), item.room_id);
    grid.appendChild(labelWrapEl("Room", roomSel));

    const qtyInput = inlineInput("Qty", item.quantity != null ? String(item.quantity) : "");
    qtyInput.type = "number";
    qtyInput.step = "any";
    grid.appendChild(labelWrap("Quantity", qtyInput));

    const unitInput = inlineInput("Unit", item.unit || "");
    grid.appendChild(labelWrap("Unit", unitInput));

    const priceWrap = document.createElement("div");
    priceWrap.style.cssText = "display:flex;flex-direction:column;gap:2px;";
    const priceLabel = document.createElement("div");
    priceLabel.style.cssText = "display:flex;align-items:center;justify-content:space-between;";
    const priceLabelText = document.createElement("label");
    priceLabelText.textContent = "Unit price $";
    priceLabelText.style.cssText = "font-size:10px;color:var(--text-muted);";
    priceLabel.appendChild(priceLabelText);
    if (defaultPrice != null) {
      const resetLink = document.createElement("button");
      resetLink.textContent = `↺ ${formatMoney(defaultPrice)}`;
      resetLink.title = "Reset to catalog default price";
      resetLink.style.cssText =
        "background:none;border:none;cursor:pointer;font-size:10px;color:var(--accent);padding:0;";
      resetLink.addEventListener("click", () => {
        priceInput.value = String(defaultPrice);
        updatePreview();
      });
      priceLabel.appendChild(resetLink);
    }
    const priceInput = inlineInput("Unit price", item.unit_price != null ? String(item.unit_price) : "");
    priceInput.type = "number";
    priceInput.step = "0.01";
    priceWrap.appendChild(priceLabel);
    priceWrap.appendChild(priceInput);
    grid.appendChild(priceWrap);

    row.appendChild(grid);

    // Live line_total preview
    const previewEl = document.createElement("div");
    previewEl.style.cssText =
      "font-size:12px;color:var(--text-muted);text-align:right;font-variant-numeric:tabular-nums;";

    const updatePreview = () => {
      const q = parseFloat(qtyInput.value);
      const p = parseFloat(priceInput.value);
      if (!isNaN(q) && !isNaN(p) && q > 0 && p > 0) {
        previewEl.textContent = `= ${formatMoney(+(q * p).toFixed(2))}`;
        previewEl.style.color = "var(--text)";
      } else {
        previewEl.textContent = "";
      }
    };
    qtyInput.addEventListener("input", updatePreview);
    priceInput.addEventListener("input", updatePreview);
    updatePreview();
    row.appendChild(previewEl);

    // Save / Cancel
    const btns = document.createElement("div");
    btns.style.cssText = "display:flex;gap:8px;justify-content:flex-end;";

    const cancelBtn = document.createElement("button");
    cancelBtn.className = "btn secondary";
    cancelBtn.textContent = "Cancel";
    cancelBtn.style.cssText = "font-size:12px;padding:4px 14px;cursor:pointer;";
    cancelBtn.addEventListener("click", () => {
      const fresh = this.renderItem(item, roomById, _listEl);
      row.replaceWith(fresh);
    });

    const saveBtn = document.createElement("button");
    saveBtn.className = "btn primary";
    saveBtn.textContent = "Save";
    saveBtn.style.cssText = "font-size:12px;padding:4px 14px;cursor:pointer;";
    saveBtn.addEventListener("click", async () => {
      const patch: Record<string, unknown> = {};
      const d = descInput.value.trim();
      if (d && d !== item.description) patch.description = d;
      const c = codeInput.value.trim();
      if (c !== (item.code || "")) patch.code = c || null;
      const q = parseFloat(qtyInput.value);
      if (!isNaN(q) && q !== item.quantity) patch.quantity = q;
      const u = unitInput.value.trim();
      if (u !== (item.unit || "")) patch.unit = u || null;
      const p = parseFloat(priceInput.value);
      if (!isNaN(p) && p !== item.unit_price) patch.unit_price = p;
      const selRoomId = (roomSel as HTMLSelectElement).value || null;
      if (selRoomId !== (item.room_id ?? null)) patch.room_id = selRoomId;

      if (Object.keys(patch).length === 0) { cancelBtn.click(); return; }

      saveBtn.disabled = true;
      saveBtn.textContent = "Saving…";
      try {
        await api.ic.patchScopeItem(this.opts.project.project_id, item.id, patch);
        await this.opts.onUpdate?.();
      } catch {
        saveBtn.textContent = "Error";
        saveBtn.disabled = false;
      }
    });

    btns.appendChild(cancelBtn);
    btns.appendChild(saveBtn);
    row.appendChild(btns);
  }

  // ── Add manual item form ───────────────────────────────────────────────────

  private renderAddItemForm(roomById: Map<string, ManifestRoom>): HTMLElement {
    const wrap = document.createElement("div");
    wrap.style.cssText = "border-top:1px solid var(--border);padding:10px 14px;";

    let formOpen = false;

    const addBtn = document.createElement("button");
    addBtn.className = "btn secondary";
    addBtn.textContent = "+ Add manual item";
    addBtn.style.cssText = "font-size:12px;cursor:pointer;";
    wrap.appendChild(addBtn);

    addBtn.addEventListener("click", () => {
      if (formOpen) return;
      formOpen = true;
      addBtn.style.display = "none";
      const form = this.buildAddForm(roomById, () => {
        formOpen = false;
        form.remove();
        addBtn.style.display = "";
      });
      wrap.appendChild(form);
    });

    return wrap;
  }

  private buildAddForm(
    roomById: Map<string, ManifestRoom>,
    onCancel: () => void,
  ): HTMLElement {
    const form = document.createElement("div");
    form.style.cssText = "display:flex;flex-direction:column;gap:8px;";

    // "Choose from catalog" button
    const catalogRow = document.createElement("div");
    catalogRow.style.cssText = "display:flex;align-items:center;gap:8px;";
    const catalogBtn = document.createElement("button");
    catalogBtn.className = "btn secondary";
    catalogBtn.textContent = "📋 Choose from catalog";
    catalogBtn.style.cssText = "font-size:12px;cursor:pointer;";
    const catalogHint = document.createElement("span");
    catalogHint.style.cssText = "font-size:11px;color:var(--text-muted);";
    catalogHint.textContent = "or fill fields manually below";
    catalogRow.appendChild(catalogBtn);
    catalogRow.appendChild(catalogHint);
    form.appendChild(catalogRow);

    const grid = document.createElement("div");
    grid.style.cssText = "display:grid;grid-template-columns:1fr 1fr;gap:6px;";

    const descInput = inlineInput("Description *", "");
    grid.appendChild(labelWrap("Description *", descInput, "1 / -1"));

    const codeInput = inlineInput("Code", "");
    grid.appendChild(labelWrap("Code", codeInput));

    const roomSel = buildRoomSelect(Array.from(roomById.values()), null);
    grid.appendChild(labelWrapEl("Room", roomSel));

    const qtyInput = inlineInput("Qty", "");
    qtyInput.type = "number";
    qtyInput.step = "any";
    grid.appendChild(labelWrap("Quantity", qtyInput));

    const unitInput = inlineInput("Unit", "");
    grid.appendChild(labelWrap("Unit", unitInput));

    const priceWrap = document.createElement("div");
    priceWrap.style.cssText = "display:flex;flex-direction:column;gap:2px;";
    const priceLbl = document.createElement("label");
    priceLbl.textContent = "Unit price $";
    priceLbl.style.cssText = "font-size:10px;color:var(--text-muted);";
    priceWrap.appendChild(priceLbl);
    const priceInput = inlineInput("Unit price", "");
    priceInput.type = "number";
    priceInput.step = "0.01";
    priceWrap.appendChild(priceInput);
    grid.appendChild(priceWrap);

    form.appendChild(grid);

    // Live total preview
    const previewEl = document.createElement("div");
    previewEl.style.cssText =
      "font-size:12px;text-align:right;font-variant-numeric:tabular-nums;color:var(--text-muted);";
    const updatePreview = () => {
      const q = parseFloat(qtyInput.value);
      const p = parseFloat(priceInput.value);
      if (!isNaN(q) && !isNaN(p) && q > 0 && p > 0) {
        previewEl.textContent = `= ${formatMoney(+(q * p).toFixed(2))}`;
        previewEl.style.color = "var(--text)";
      } else {
        previewEl.textContent = "";
      }
    };
    qtyInput.addEventListener("input", updatePreview);
    priceInput.addEventListener("input", updatePreview);
    form.appendChild(previewEl);

    // Wire catalog picker
    catalogBtn.addEventListener("click", () => {
      if (!this.catalog) {
        catalogHint.textContent = "Catalog not loaded yet…";
        return;
      }
      openCatalogPicker(this.catalog, (selected) => {
        descInput.value = selected.description;
        codeInput.value = selected.code;
        unitInput.value = selected.unit;
        priceInput.value = String(selected.default_unit_price);
        updatePreview();
        descInput.focus();
      });
    });

    // Buttons
    const btns = document.createElement("div");
    btns.style.cssText = "display:flex;gap:8px;justify-content:flex-end;align-items:center;";
    const errMsg = document.createElement("span");
    errMsg.style.cssText = "font-size:11px;color:#ff4a4a;flex:1;";
    btns.appendChild(errMsg);

    const cancelBtn = document.createElement("button");
    cancelBtn.className = "btn secondary";
    cancelBtn.textContent = "Cancel";
    cancelBtn.style.cssText = "font-size:12px;padding:4px 14px;cursor:pointer;";
    cancelBtn.addEventListener("click", onCancel);

    const addBtn = document.createElement("button");
    addBtn.className = "btn primary";
    addBtn.textContent = "Add";
    addBtn.style.cssText = "font-size:12px;padding:4px 14px;cursor:pointer;";
    addBtn.addEventListener("click", async () => {
      const desc = descInput.value.trim();
      if (!desc) { errMsg.textContent = "Description is required"; return; }
      addBtn.disabled = true;
      addBtn.textContent = "Adding…";

      const item: Record<string, unknown> = {
        description: desc,
        source: "manual",
        scope_id: crypto.randomUUID(),
      };
      const c = codeInput.value.trim();
      if (c) item.code = c;
      const selRoomId = (roomSel as HTMLSelectElement).value;
      if (selRoomId) {
        item.room_id = selRoomId;
        const r = roomById.get(selRoomId);
        if (r) item.room_name = r.name;
      }
      const q = parseFloat(qtyInput.value);
      if (!isNaN(q)) item.quantity = q;
      const u = unitInput.value.trim();
      if (u) item.unit = u;
      const p = parseFloat(priceInput.value);
      if (!isNaN(p)) item.unit_price = p;

      try {
        await api.ic.addScopeItems(
          this.opts.project.project_id,
          [item as Parameters<typeof api.ic.addScopeItems>[1][0]],
          crypto.randomUUID(),
        );
        await this.opts.onUpdate?.();
      } catch {
        errMsg.textContent = "Error — try again";
        addBtn.textContent = "Add";
        addBtn.disabled = false;
      }
    });

    btns.appendChild(cancelBtn);
    btns.appendChild(addBtn);
    form.appendChild(btns);
    return form;
  }

  // ── Mutations ──────────────────────────────────────────────────────────────

  private async toggleReject(item: ManifestScopeItem, rejected: boolean): Promise<void> {
    try {
      await api.ic.patchScopeItem(this.opts.project.project_id, item.id, { rejected });
      await this.opts.onUpdate?.();
    } catch { /* silent */ }
  }

  private async deleteItem(item: ManifestScopeItem): Promise<void> {
    if (!confirm(`Delete "${item.description || item.code || "this item"}"?`)) return;
    try {
      await api.ic.deleteScopeItem(this.opts.project.project_id, item.id);
      await this.opts.onUpdate?.();
    } catch { /* silent */ }
  }

  // ── Totals ─────────────────────────────────────────────────────────────────

  private renderTotals(): HTMLElement {
    const m = this.opts.manifest!;
    const e = m.estimate;
    const cfg = m.estimate_config;

    const card = makeCard();
    card.style.padding = "14px 16px";
    card.style.display = "flex";
    card.style.flexDirection = "column";
    card.style.gap = "6px";

    const heading = document.createElement("div");
    heading.style.cssText =
      "font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.4px;color:var(--text-muted);margin-bottom:2px;";
    heading.textContent = "Totals";
    card.appendChild(heading);

    if (e) {
      card.appendChild(totalsRow("Subtotal", formatMoney(e.subtotal)));
      if (e.overhead_percent || e.overhead_amount) {
        card.appendChild(totalsRow(`Overhead ${e.overhead_percent}%`, formatMoney(e.overhead_amount)));
      }
      if (e.profit_percent || e.profit_amount) {
        card.appendChild(totalsRow(`Profit ${e.profit_percent}%`, formatMoney(e.profit_amount)));
      }
      if (e.markup_percent != null && !e.overhead_percent && !e.profit_percent) {
        card.appendChild(totalsRow(`Markup ${e.markup_percent}%`, formatMoney(e.markup_amount ?? 0)));
      }
      if (e.tax_percent) {
        card.appendChild(totalsRow(`Tax ${e.tax_percent}%`, formatMoney(e.tax_amount)));
      }
      const grand = totalsRow("Grand total", formatMoney(e.grand_total));
      grand.style.borderTop = "1px solid var(--border)";
      grand.style.marginTop = "4px";
      grand.style.paddingTop = "8px";
      grand.style.fontWeight = "700";
      grand.style.fontSize = "15px";
      card.appendChild(grand);
      if (e.last_modified) {
        const ts = document.createElement("div");
        ts.textContent = `Last modified ${new Date(e.last_modified).toLocaleString()}`;
        ts.style.cssText = "font-size:11px;color:var(--text-muted);margin-top:4px;text-align:right;";
        card.appendChild(ts);
      }
    } else {
      const sum = (m.scope || []).filter((i) => !i.rejected).reduce((s, i) => s + (i.line_total ?? 0), 0);
      const note = document.createElement("div");
      note.textContent = "No estimate computed yet — sum of line totals:";
      note.style.cssText = "font-size:11px;color:var(--text-muted);";
      card.appendChild(note);
      card.appendChild(totalsRow("Sum of line items", formatMoney(sum)));
    }

    card.appendChild(this.renderEstimateConfigEditor(cfg));
    return card;
  }

  private renderEstimateConfigEditor(cfg: EstimateConfig | null | undefined): HTMLElement {
    const wrap = document.createElement("div");
    wrap.style.cssText =
      "margin-top:12px;padding-top:12px;border-top:1px solid var(--border);display:flex;flex-direction:column;gap:8px;";

    const title = document.createElement("div");
    title.style.cssText =
      "font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.4px;color:var(--text-muted);";
    title.textContent = "Rate overrides (%)";
    wrap.appendChild(title);

    const grid = document.createElement("div");
    grid.style.cssText = "display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;";
    const overheadInput = configPctInput("Overhead", cfg?.overhead_pct ?? 0);
    const profitInput = configPctInput("Profit", cfg?.profit_pct ?? 0);
    const taxInput = configPctInput("Tax", cfg?.tax_pct ?? 0);
    grid.appendChild(overheadInput.wrap);
    grid.appendChild(profitInput.wrap);
    grid.appendChild(taxInput.wrap);
    wrap.appendChild(grid);

    const footer = document.createElement("div");
    footer.style.cssText = "display:flex;align-items:center;gap:8px;justify-content:flex-end;";
    const hint = document.createElement("span");
    hint.style.cssText = "font-size:11px;color:var(--text-muted);flex:1;";
    hint.textContent = cfg ? "Per-project overrides active" : "Using global defaults";
    footer.appendChild(hint);

    const saveBtn = document.createElement("button");
    saveBtn.className = "btn primary";
    saveBtn.textContent = "Save rates";
    saveBtn.style.cssText = "font-size:12px;padding:4px 14px;cursor:pointer;";
    saveBtn.addEventListener("click", async () => {
      const overhead = parseFloat(overheadInput.input.value);
      const profit = parseFloat(profitInput.input.value);
      const tax = parseFloat(taxInput.input.value);
      if (isNaN(overhead) || isNaN(profit) || isNaN(tax)) {
        hint.textContent = "Invalid values";
        hint.style.color = "#ff4a4a";
        return;
      }
      saveBtn.disabled = true;
      saveBtn.textContent = "Saving…";
      try {
        await api.ic.patchEstimateConfig(this.opts.project.project_id, {
          overhead_pct: overhead, profit_pct: profit, tax_pct: tax,
        });
        await this.opts.onUpdate?.();
      } catch {
        hint.textContent = "Error — try again";
        hint.style.color = "#ff4a4a";
        saveBtn.textContent = "Save rates";
        saveBtn.disabled = false;
      }
    });
    footer.appendChild(saveBtn);
    wrap.appendChild(footer);
    return wrap;
  }

  // ── Action buttons ─────────────────────────────────────────────────────────

  private renderActions(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.style.cssText = "display:flex;gap:8px;flex-wrap:wrap;";

    const esxBtn = actionBtn("↓ Download Xactimate ESX", "btn primary");
    esxBtn.addEventListener("click", async () => {
      esxBtn.disabled = true;
      esxBtn.textContent = "Downloading…";
      try {
        const url = await api.ic.downloadEstimateEsx(this.opts.project.project_id);
        dl(url, "estimate.esx");
      } catch {
        esxBtn.textContent = "Error — try again";
      } finally {
        if (!esxBtn.textContent.startsWith("Error")) esxBtn.textContent = "↓ Download Xactimate ESX";
        esxBtn.disabled = false;
      }
    });
    wrap.appendChild(esxBtn);

    const pdfBtn = actionBtn("↓ Download Estimate PDF", "btn secondary");
    pdfBtn.addEventListener("click", async () => {
      pdfBtn.disabled = true;
      pdfBtn.textContent = "Generating…";
      try {
        const url = await api.ic.downloadEstimatePdf(this.opts.project.project_id);
        dl(url, "estimate.pdf");
      } catch {
        pdfBtn.textContent = "Error — try again";
      } finally {
        if (!pdfBtn.textContent.startsWith("Error")) pdfBtn.textContent = "↓ Download Estimate PDF";
        pdfBtn.disabled = false;
      }
    });
    wrap.appendChild(pdfBtn);

    const autoBtn = actionBtn("✦ Auto-generate estimate", "btn secondary");
    autoBtn.title = "Run classification → sizing → DAG pipeline";
    autoBtn.addEventListener("click", async () => {
      if (!confirm("Re-run the auto-generate pipeline? Existing Auto items will be replaced.")) return;
      autoBtn.disabled = true;
      autoBtn.textContent = "Running…";
      try {
        const result = await api.ic.autoGenerateEstimate(this.opts.project.project_id);
        autoBtn.textContent = `✦ ${(result.items as unknown[]).length} items generated`;
        await this.opts.onUpdate?.();
        setTimeout(() => { autoBtn.textContent = "✦ Auto-generate estimate"; }, 3000);
      } catch {
        autoBtn.textContent = "Error — try again";
        setTimeout(() => { autoBtn.textContent = "✦ Auto-generate estimate"; }, 3000);
      } finally {
        autoBtn.disabled = false;
      }
    });
    wrap.appendChild(autoBtn);

    return wrap;
  }
}

// ── Catalog picker modal ──────────────────────────────────────────────────────

function openCatalogPicker(
  catalog: ScopeCatalog,
  onSelect: (item: ScopeCatalogItem) => void,
): void {
  // Backdrop
  const backdrop = document.createElement("div");
  backdrop.style.cssText =
    "position:fixed;inset:0;background:rgba(0,0,0,.55);z-index:9000;display:flex;align-items:center;justify-content:center;";

  const modal = document.createElement("div");
  modal.style.cssText = `
    background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);
    width:min(600px,94vw);max-height:80vh;display:flex;flex-direction:column;
    box-shadow:0 8px 40px rgba(0,0,0,.45);
  `;

  // Header
  const hdr = document.createElement("div");
  hdr.style.cssText =
    "display:flex;align-items:center;gap:10px;padding:12px 16px;border-bottom:1px solid var(--border);";
  const searchInput = document.createElement("input");
  searchInput.type = "text";
  searchInput.placeholder = "Search codes, descriptions, keywords…";
  searchInput.style.cssText =
    "flex:1;background:var(--bg);border:1px solid var(--border);border-radius:4px;padding:6px 10px;font-size:13px;color:var(--text);outline:none;";
  searchInput.addEventListener("focus", () => (searchInput.style.borderColor = "var(--accent)"));
  searchInput.addEventListener("blur", () => (searchInput.style.borderColor = "var(--border)"));
  const closeBtn = document.createElement("button");
  closeBtn.textContent = "✕";
  closeBtn.style.cssText =
    "background:none;border:none;cursor:pointer;font-size:16px;color:var(--text-muted);padding:2px 6px;";
  closeBtn.addEventListener("click", () => backdrop.remove());
  hdr.appendChild(searchInput);
  hdr.appendChild(closeBtn);
  modal.appendChild(hdr);

  // Category filter row
  const filterRow = document.createElement("div");
  filterRow.style.cssText =
    "display:flex;gap:6px;padding:8px 16px;flex-wrap:wrap;border-bottom:1px solid var(--border);overflow-x:auto;";
  let activeCat: string | null = null;

  const catBtns: HTMLButtonElement[] = [];

  const allBtn = document.createElement("button");
  allBtn.textContent = "All";
  allBtn.style.cssText = catChipStyle(true);
  allBtn.addEventListener("click", () => {
    activeCat = null;
    catBtns.forEach((b) => (b.style.cssText = catChipStyle(false)));
    allBtn.style.cssText = catChipStyle(true);
    renderList("");
  });
  filterRow.appendChild(allBtn);

  for (const cat of catalog.categories) {
    const btn = document.createElement("button");
    btn.textContent = cat.label;
    btn.style.cssText = catChipStyle(false);
    btn.addEventListener("click", () => {
      activeCat = cat.key;
      catBtns.forEach((b) => (b.style.cssText = catChipStyle(false)));
      allBtn.style.cssText = catChipStyle(false);
      btn.style.cssText = catChipStyle(true);
      renderList(searchInput.value);
    });
    catBtns.push(btn);
    filterRow.appendChild(btn);
  }
  modal.appendChild(filterRow);

  // Item list
  const listEl = document.createElement("div");
  listEl.style.cssText = "overflow-y:auto;flex:1;";
  modal.appendChild(listEl);

  const renderList = (query: string) => {
    listEl.innerHTML = "";
    const q = query.toLowerCase().trim();

    const matchedCats = catalog.categories
      .filter((c) => activeCat === null || c.key === activeCat)
      .map((c) => ({
        ...c,
        items: c.items.filter((i) =>
          !q ||
          i.description.toLowerCase().includes(q) ||
          i.code.toLowerCase().includes(q) ||
          i.keywords.some((k) => k.toLowerCase().includes(q)),
        ),
      }))
      .filter((c) => c.items.length > 0);

    if (matchedCats.length === 0) {
      const empty = document.createElement("div");
      empty.style.cssText =
        "padding:32px;text-align:center;color:var(--text-muted);font-size:13px;";
      empty.textContent = q ? `No items matching "${query}"` : "No items";
      listEl.appendChild(empty);
      return;
    }

    for (const cat of matchedCats) {
      const catHdr = document.createElement("div");
      catHdr.textContent = cat.label;
      catHdr.style.cssText =
        "padding:6px 16px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:var(--text-muted);background:var(--surface2);position:sticky;top:0;z-index:1;";
      listEl.appendChild(catHdr);

      for (const item of cat.items) {
        const row = document.createElement("div");
        row.style.cssText =
          "display:flex;align-items:baseline;gap:10px;padding:9px 16px;border-top:1px solid var(--border);cursor:pointer;";
        row.addEventListener("mouseenter", () => (row.style.background = "var(--surface2)"));
        row.addEventListener("mouseleave", () => (row.style.background = ""));
        row.addEventListener("click", () => {
          onSelect(item);
          backdrop.remove();
        });

        const codeEl = document.createElement("code");
        codeEl.textContent = item.code;
        codeEl.style.cssText =
          "font-size:11px;background:var(--bg);border:1px solid var(--border);padding:1px 6px;border-radius:4px;color:var(--text-muted);flex-shrink:0;";

        const descEl = document.createElement("span");
        descEl.textContent = item.description;
        descEl.style.cssText = "font-size:13px;color:var(--text);flex:1;min-width:0;";

        const meta = document.createElement("span");
        meta.textContent = `${item.unit} · ${formatMoney(item.default_unit_price)}`;
        meta.style.cssText =
          "font-size:11px;color:var(--text-muted);white-space:nowrap;font-variant-numeric:tabular-nums;";

        row.appendChild(codeEl);
        row.appendChild(descEl);
        row.appendChild(meta);
        listEl.appendChild(row);
      }
    }
  };

  searchInput.addEventListener("input", () => renderList(searchInput.value));
  renderList("");

  backdrop.addEventListener("click", (e) => {
    if (e.target === backdrop) backdrop.remove();
  });
  backdrop.appendChild(modal);
  document.body.appendChild(backdrop);
  setTimeout(() => searchInput.focus(), 50);
}

// ── helpers ───────────────────────────────────────────────────────────────────

function makeCard(): HTMLElement {
  const c = document.createElement("section");
  c.style.cssText =
    "background:var(--surface);border:1px solid var(--border);border-radius:var(--radius);overflow:hidden;";
  return c;
}

function makeGroupHeader(source: string, count: number, subtotal: number): HTMLElement {
  const hdr = document.createElement("div");
  hdr.style.cssText =
    "display:flex;align-items:center;gap:8px;padding:10px 14px;border-bottom:1px solid var(--border);background:var(--surface2);";
  const dot = document.createElement("span");
  dot.style.cssText = `width:8px;height:8px;border-radius:50%;background:${SOURCE_COLORS[source] || "#666"};flex-shrink:0;`;
  const lbl = document.createElement("span");
  lbl.textContent = SOURCE_LABELS[source] || source;
  lbl.style.cssText =
    "font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.4px;color:var(--text);";
  const cnt = document.createElement("span");
  cnt.textContent = String(count);
  cnt.style.cssText =
    "font-size:11px;color:var(--text-muted);background:var(--border);padding:1px 7px;border-radius:99px;";
  const sub = document.createElement("span");
  sub.textContent = formatMoney(subtotal);
  sub.style.cssText =
    "margin-left:auto;font-size:13px;font-weight:600;font-variant-numeric:tabular-nums;color:var(--text);";
  hdr.appendChild(dot);
  hdr.appendChild(lbl);
  hdr.appendChild(cnt);
  hdr.appendChild(sub);
  return hdr;
}

function badge(text: string, bg: string, color: string): HTMLElement {
  const el = document.createElement("span");
  el.textContent = text;
  el.style.cssText = `font-size:10px;background:${bg};color:${color};padding:1px 6px;border-radius:99px;font-weight:600;text-decoration:none;flex-shrink:0;`;
  return el;
}

function iconBtn(icon: string, title: string): HTMLButtonElement {
  const b = document.createElement("button");
  b.textContent = icon;
  b.title = title;
  b.style.cssText =
    "background:none;border:none;cursor:pointer;font-size:13px;padding:2px 4px;color:var(--text-muted);line-height:1;";
  b.addEventListener("mouseenter", () => (b.style.color = "var(--text)"));
  b.addEventListener("mouseleave", () => (b.style.color = "var(--text-muted)"));
  return b;
}

function stepperBtn(label: string): HTMLButtonElement {
  const b = document.createElement("button");
  b.textContent = label;
  b.style.cssText =
    "background:var(--bg);border:1px solid var(--border);border-radius:3px;width:20px;height:18px;cursor:pointer;font-size:12px;line-height:1;padding:0;color:var(--text-muted);";
  b.addEventListener("mouseenter", () => (b.style.color = "var(--text)"));
  b.addEventListener("mouseleave", () => (b.style.color = "var(--text-muted)"));
  return b;
}

function actionBtn(text: string, className: string): HTMLButtonElement {
  const b = document.createElement("button");
  b.className = className;
  b.textContent = text;
  b.style.cssText = "display:inline-flex;align-items:center;gap:6px;cursor:pointer;";
  return b;
}

function inlineInput(placeholder: string, value: string): HTMLInputElement {
  const inp = document.createElement("input");
  inp.type = "text";
  inp.value = value;
  inp.placeholder = placeholder;
  inp.style.cssText =
    "width:100%;box-sizing:border-box;background:var(--bg);border:1px solid var(--border);border-radius:4px;padding:4px 8px;font-size:12px;color:var(--text);outline:none;";
  inp.addEventListener("focus", () => (inp.style.borderColor = "var(--accent)"));
  inp.addEventListener("blur", () => (inp.style.borderColor = "var(--border)"));
  return inp;
}

function labelWrap(label: string, inp: HTMLInputElement, gridColumn?: string): HTMLElement {
  const w = document.createElement("div");
  w.style.cssText = `display:flex;flex-direction:column;gap:2px;${gridColumn ? `grid-column:${gridColumn};` : ""}`;
  const lbl = document.createElement("label");
  lbl.textContent = label;
  lbl.style.cssText = "font-size:10px;color:var(--text-muted);";
  w.appendChild(lbl);
  w.appendChild(inp);
  return w;
}

function labelWrapEl(label: string, el: HTMLElement): HTMLElement {
  const w = document.createElement("div");
  w.style.cssText = "display:flex;flex-direction:column;gap:2px;";
  const lbl = document.createElement("label");
  lbl.textContent = label;
  lbl.style.cssText = "font-size:10px;color:var(--text-muted);";
  w.appendChild(lbl);
  w.appendChild(el);
  return w;
}

function buildRoomSelect(rooms: ManifestRoom[], selectedId: string | null): HTMLElement {
  const sel = document.createElement("select");
  sel.style.cssText =
    "width:100%;box-sizing:border-box;background:var(--bg);border:1px solid var(--border);border-radius:4px;padding:4px 8px;font-size:12px;color:var(--text);outline:none;";
  sel.addEventListener("focus", () => (sel.style.borderColor = "var(--accent)"));
  sel.addEventListener("blur", () => (sel.style.borderColor = "var(--border)"));
  const empty = document.createElement("option");
  empty.value = "";
  empty.textContent = "— General (no room) —";
  sel.appendChild(empty);
  for (const r of rooms) {
    const opt = document.createElement("option");
    opt.value = r.id;
    opt.textContent = r.name || r.id;
    if (r.id === selectedId) opt.selected = true;
    sel.appendChild(opt);
  }
  return sel;
}

function configPctInput(label: string, value: number): { wrap: HTMLElement; input: HTMLInputElement } {
  const input = document.createElement("input");
  input.type = "number";
  input.step = "0.1";
  input.min = "0";
  input.max = "100";
  input.value = String(value);
  input.style.cssText =
    "width:100%;box-sizing:border-box;background:var(--bg);border:1px solid var(--border);border-radius:4px;padding:4px 8px;font-size:12px;color:var(--text);outline:none;";
  input.addEventListener("focus", () => (input.style.borderColor = "var(--accent)"));
  input.addEventListener("blur", () => (input.style.borderColor = "var(--border)"));
  const wrap = document.createElement("div");
  wrap.style.cssText = "display:flex;flex-direction:column;gap:2px;";
  const lbl = document.createElement("label");
  lbl.textContent = label;
  lbl.style.cssText = "font-size:10px;color:var(--text-muted);";
  wrap.appendChild(lbl);
  wrap.appendChild(input);
  return { wrap, input };
}

function totalsRow(label: string, value: string): HTMLElement {
  const row = document.createElement("div");
  row.style.cssText =
    "display:flex;justify-content:space-between;font-size:13px;color:var(--text);font-variant-numeric:tabular-nums;";
  const l = document.createElement("span");
  l.textContent = label;
  l.style.color = "var(--text-muted)";
  const v = document.createElement("span");
  v.textContent = value;
  row.appendChild(l);
  row.appendChild(v);
  return row;
}

function emptyMessage(text: string): HTMLElement {
  const w = document.createElement("div");
  w.style.cssText =
    "padding:32px;color:var(--text-muted);text-align:center;font-size:13px;line-height:1.5;";
  w.textContent = text;
  return w;
}

function catChipStyle(active: boolean): string {
  return active
    ? "background:var(--accent);color:#fff;border:none;border-radius:99px;padding:3px 10px;font-size:11px;cursor:pointer;white-space:nowrap;"
    : "background:var(--surface2);color:var(--text-muted);border:1px solid var(--border);border-radius:99px;padding:3px 10px;font-size:11px;cursor:pointer;white-space:nowrap;";
}

function dl(url: string, filename: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function formatMoney(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}

function formatNumber(n: number): string {
  return n.toLocaleString("en-US", { maximumFractionDigits: 4 });
}
